import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Registration : famille d'entités "inscription".
 *
 * Lien entre un `contact` et un `event` (ou une session) : place demandée, en
 * liste d'attente, confirmée ou annulée, avec son paiement éventuel. Construite
 * sur le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas d'inscription concrète ("cours d'essai", "atelier") :
 * il connaît la famille générique `registration`. Le vocabulaire métier est
 * porté par le champ `type` (chaîne libre déclarée par l'app), et l'état du
 * cycle de vie (attente, confirmé, annulé) par `status`.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `eventId` et `contactId` sont le CŒUR de la famille, donc requis ; `groupId`
 * et `paymentId` sont optionnels. Les liens attendance / credit ne vivent PAS
 * ici : c'est l'autre entité qui porte un `registrationId`.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille registration (hors champs standard du socle). */
export const registrationCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "event", "session"). */
  type: z.string().min(1),
  /** Événement ou session visé (référence `event`, cœur de la famille). */
  eventId: z.string().min(1),
  /** Contact inscrit (référence `contact`, cœur de la famille). */
  contactId: z.string().min(1),
  /** État du cycle de vie, chaîne libre pilotée par l'app (ex. "attente", "confirme", "annule"). */
  status: z.string().min(1).optional(),
  /** Rang dans la liste d'attente, optionnel. */
  waitlistPosition: z.number().int().optional(),
  /** Groupe d'inscription (référence `group`), optionnel. */
  groupId: z.string().min(1).optional(),
  /** Paiement rattaché (référence `payment`), optionnel. */
  paymentId: z.string().min(1).optional(),
  /** Canal d'origine de l'inscription (ex. "web", "guichet"), optionnel. */
  source: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille registration (champs standard + champs métier). */
export const RegistrationSchema = z.object({
  ...baseEntityFields,
  ...registrationCoreFields,
});

/** Type d'une registration. */
export type Registration = z.infer<typeof RegistrationSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const RegistrationCreateInputSchema = z.object({
  ...registrationCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une registration. */
export type RegistrationCreateInput = z.infer<typeof RegistrationCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const RegistrationUpdateInputSchema = z
  .object({ ...registrationCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une registration. */
export type RegistrationUpdateInput = z.infer<typeof RegistrationUpdateInputSchema>;
